import React, { useEffect, useMemo, useState } from 'react';

const hourOptions = Array.from({ length: 24 }).map((_, i) => String(i).padStart(2, '0') + ':00');

function parsePhones(text) {
  return String(text || '')
    .split(/[\n,;]+/g)
    .map((s) => s.trim())
    .filter(Boolean);
}

export default function BulkMessagePanel({ firmSlug }) {
  const [tab, setTab] = useState('contacts'); // contacts | messages | status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState('');
  const [newListName, setNewListName] = useState('');
  const [addToListId, setAddToListId] = useState('');

  const [contacts, setContacts] = useState([]);
  const [contactsTotal, setContactsTotal] = useState(0);
  const [contactsPage, setContactsPage] = useState(1);
  const [contactsQuery, setContactsQuery] = useState('');
  const [importReport, setImportReport] = useState(null);
  const [addPhone, setAddPhone] = useState('');
  const [addName, setAddName] = useState('');
  const [editingContactId, setEditingContactId] = useState(null);
  const [editPhone, setEditPhone] = useState('');
  const [editName, setEditName] = useState('');
  const [editListId, setEditListId] = useState('');

  const [campaigns, setCampaigns] = useState([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);

  const [campaignName, setCampaignName] = useState('');
  const [campaignMessage, setCampaignMessage] = useState('');
  const [recipientMode, setRecipientMode] = useState('manual'); // manual | contact_list
  const [manualPhones, setManualPhones] = useState('');
  const [selectedListIds, setSelectedListIds] = useState([]); // multi-select
  const [startHour, setStartHour] = useState('09:00');
  const [endHour, setEndHour] = useState('18:00');
  const [days, setDays] = useState([1, 2, 3, 4, 5]); // 1=Pzt .. 7=Paz

  const [dailyStatus, setDailyStatus] = useState(null);

  const headers = useMemo(() => ({ 'x-firm-slug': firmSlug }), [firmSlug]);

  // Responsive kontrolü
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const dailyCapacity = useMemo(() => {
    const sh = Number(startHour.split(':')[0]);
    const eh = Number(endHour.split(':')[0]);
    return Math.max(0, eh - sh) * 10;
  }, [startHour, endHour]);

  const campaignRecipientCount = useMemo(() => {
    if (recipientMode === 'manual') return parsePhones(manualPhones).length;
    const selected = new Set((selectedListIds || []).map(Number));
    return (lists || [])
      .filter((l) => selected.has(Number(l.id)))
      .reduce((sum, l) => sum + Number(l.contact_count || 0), 0);
  }, [recipientMode, manualPhones, selectedListIds, lists]);

  const campaignRecipientLabel = useMemo(() => {
    if (recipientMode === 'manual') return 'Manuel alıcı sayısı';
    return 'Seçili grup alıcı sayısı';
  }, [recipientMode]);

  const statusTr = (status) => {
    const s = String(status || '').toLowerCase();
    const map = {
      draft: 'Taslak',
      queued: 'Sırada',
      sending: 'Gönderiliyor',
      paused: 'Durduruldu',
      cancelled: 'İptal edildi',
      completed: 'Tamamlandı',
    };
    return map[s] || status;
  };

  const recipientStatusTr = (status) => {
    const s = String(status || '').toLowerCase();
    const map = {
      pending: 'Beklemede',
      sent: 'Gönderildi',
      failed: 'Hatalı',
    };
    return map[s] || status;
  };

  async function apiGet(path) {
    const res = await fetch(path, { headers });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    return data;
  }

  async function apiSend(path, method, body) {
    const res = await fetch(path, {
      method,
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data?.success === false) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
    return data;
  }

  async function loadLists() {
    const d = await apiGet('/api/bulk/lists');
    setLists(d.data || []);
    // varsayılan: manuel eklemede seçili grup yoksa filtrelenen grubu baz al
    if (!addToListId && selectedListId) setAddToListId(selectedListId);
  }

  async function loadContacts(page = contactsPage) {
    const params = new URLSearchParams();
    if (selectedListId) params.set('list_id', selectedListId);
    if (contactsQuery) params.set('q', contactsQuery);
    params.set('page', String(page));
    params.set('limit', '50');
    const d = await apiGet('/api/bulk/contacts?' + params.toString());
    setContacts(d.data?.rows || []);
    setContactsTotal(d.data?.total || 0);
    setContactsPage(d.data?.page || page);
  }

  async function loadCampaigns() {
    const d = await apiGet('/api/bulk/campaigns?page=1&limit=50');
    setCampaigns(d.data?.rows || []);
    setCampaignsTotal(d.data?.total || 0);
  }

  async function loadDailyStatus() {
    const d = await apiGet('/api/bulk/status/daily');
    setDailyStatus(d.data || null);
  }

  useEffect(() => {
    if (!firmSlug) return;
    (async () => {
      setError('');
      setInfo('');
      setLoading(true);
      try {
        await loadLists();
        await loadContacts(1);
        await loadCampaigns();
        await loadDailyStatus();
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [firmSlug]);

  return (
    <div style={{ width: '100%', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: isMobile ? 'wrap' : 'nowrap', flexDirection: isMobile ? 'column' : 'row', width: '100%', maxWidth: '100%', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', flex: isMobile ? '1' : '0 0 auto' }}>
          <button
            onClick={() => setTab('contacts')}
            style={{
              padding: isMobile ? '8px 12px' : '10px 14px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              background: tab === 'contacts' ? '#667eea' : '#fff',
              color: tab === 'contacts' ? '#fff' : '#333',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: isMobile ? '14px' : '16px',
              flex: isMobile ? '1' : '0',
            }}
          >
            Rehber
          </button>
          <button
            onClick={() => setTab('messages')}
            style={{
              padding: isMobile ? '8px 12px' : '10px 14px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              background: tab === 'messages' ? '#667eea' : '#fff',
              color: tab === 'messages' ? '#fff' : '#333',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: isMobile ? '14px' : '16px',
              flex: isMobile ? '1' : '0',
            }}
          >
            Toplu Mesajlar
          </button>

          <button
            onClick={() => setTab('status')}
            style={{
              padding: isMobile ? '8px 12px' : '10px 14px',
              borderRadius: '8px',
              border: '1px solid #ddd',
              background: tab === 'status' ? '#667eea' : '#fff',
              color: tab === 'status' ? '#fff' : '#333',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: isMobile ? '14px' : '16px',
              flex: isMobile ? '1' : '0',
            }}
          >
            Durum
          </button>
        </div>

        <div style={{ 
          marginLeft: isMobile ? '0' : 'auto', 
          marginTop: isMobile ? '8px' : '0',
          color: '#666', 
          fontSize: isMobile ? '12px' : '13px', 
          alignSelf: isMobile ? 'flex-start' : 'center',
          width: isMobile ? '100%' : 'auto',
        }}>
          Saatlik limit: <strong>10</strong> mesaj • Bugün kapasite: <strong>{dailyCapacity}</strong> mesaj
        </div>
      </div>

      {error && (
        <div style={{ background: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '10px', marginBottom: '12px' }}>
          {error}
        </div>
      )}
      {info && (
        <div style={{ background: '#d4edda', color: '#155724', padding: '12px', borderRadius: '10px', marginBottom: '12px' }}>
          {info}
        </div>
      )}

      {loading && <div style={{ padding: '10px', color: '#666' }}>Yükleniyor...</div>}

      {tab === 'contacts' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: isMobile ? '12px' : '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <div style={{ fontWeight: 700, marginBottom: '10px' }}>Müşteri Grubu</div>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <select value={selectedListId} onChange={(e) => setSelectedListId(e.target.value)} style={{ padding: '10px', borderRadius: '8px' }}>
                <option value="">(Tüm rehber)</option>
                {lists.map((l) => (
                  <option key={l.id} value={String(l.id)}>
                    {l.name} ({l.contact_count ?? 0})
                  </option>
                ))}
              </select>
              <button
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    await loadContacts(1);
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                Yenile
              </button>
              <button
                onClick={() => {
                  const qs = selectedListId ? `?list_id=${encodeURIComponent(selectedListId)}` : '';
                  window.open('/api/bulk/contacts/export' + qs, '_blank');
                }}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                CSV Export
              </button>

              <button
                onClick={() => window.open('/api/bulk/contacts/template.csv', '_blank')}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                CSV Örneği İndir
              </button>

              <button
                onClick={() => window.open('/api/bulk/contacts/template.xls', '_blank')}
                style={{ padding: '10px 14px', borderRadius: '8px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                Excel Örneği İndir
              </button>
            </div>

            <div style={{ marginTop: '12px', display: 'flex', gap: isMobile ? '6px' : '10px', flexWrap: isMobile ? 'wrap' : 'nowrap', alignItems: 'stretch' }}>
              <input
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                placeholder="Yeni müşteri grubu adı"
                style={{ 
                  flex: isMobile ? '1 1 calc(100% - 80px)' : 1, 
                  minWidth: 0,
                  padding: isMobile ? '8px' : '10px', 
                  borderRadius: '8px', 
                  border: '1px solid #ddd',
                  fontSize: isMobile ? '14px' : '16px'
                }}
              />
              <button
                onClick={async () => {
                  if (!newListName.trim()) return;
                  setLoading(true);
                  setError('');
                  try {
                    await apiSend('/api/bulk/lists', 'POST', { name: newListName.trim() });
                    setNewListName('');
                    await loadLists();
                    setInfo('Liste oluşturuldu.');
                    setTimeout(() => setInfo(''), 2000);
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ 
                  padding: isMobile ? '8px 12px' : '10px 14px', 
                  borderRadius: '8px', 
                  border: 'none', 
                  background: '#28a745', 
                  color: '#fff', 
                  cursor: 'pointer', 
                  fontWeight: 700,
                  fontSize: isMobile ? '14px' : '16px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                Oluştur
              </button>
            </div>

            {/* Grup özeti + progress */}
            <div style={{ marginTop: '14px', fontSize: '13px', color: '#666' }}>
              Gruplar:
            </div>
            <div style={{ marginTop: '8px', border: '1px solid #f1f1f1', borderRadius: '10px', maxHeight: '220px', overflow: 'auto', width: '100%', maxWidth: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px', tableLayout: isMobile ? 'auto' : 'auto' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Grup</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Kişi</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Gönderi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lists.map((l) => {
                      const prog = l.progress;
                      const statusText =
                        prog?.status === 'devam_ediyor' ? 'Devam ediyor' :
                        prog?.status === 'beklemede' ? 'Beklemede' :
                        prog?.status === 'durduruldu' ? 'Durduruldu' :
                        '';
                      const progressText = prog ? `${prog.done}/${prog.total}` : '';
                      return (
                        <tr key={l.id} style={{ borderTop: '1px solid #eee', cursor: 'pointer' }} onClick={() => setSelectedListId(String(l.id))}>
                          <td style={{ padding: isMobile ? '8px' : '10px', fontWeight: 700 }}>{l.name}</td>
                          <td style={{ padding: isMobile ? '8px' : '10px' }}>{l.contact_count ?? 0}</td>
                          <td style={{ padding: isMobile ? '8px' : '10px', color: prog ? '#333' : '#999' }}>
                          {prog ? (
                            <span>
                              <strong>{progressText}</strong> {statusText ? `• ${statusText}` : ''}
                            </span>
                          ) : (
                            <span>-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {lists.length === 0 && (
                    <tr>
                      <td colSpan={3} style={{ padding: '12px', color: '#666' }}>Henüz grup yok.</td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>

            <div style={{ marginTop: '18px', fontWeight: 700 }}>CSV Import</div>
            <div style={{ fontSize: '13px', color: '#666', margin: '6px 0 10px' }}>
              Desteklenen formatlar:
              <div>
                - Basit: <code>phone</code>, opsiyonel <code>name</code>, opsiyonel <code>group</code> (Müşteri Grubu)
              </div>
              <div>
                - OpenCart export: <code>telephone</code> ve opsiyonel <code>firstname</code>/<code>lastname</code> (diğer sütunlar yok sayılır)
              </div>
              <div>
                - OpenCart export’ta grup varsa: <code>customer_group</code> / <code>customer_group_name</code> algılanır ve ilgili Müşteri Grubu’na eklenir (grup yoksa otomatik oluşturulur)
              </div>
              Mükerrerler otomatik tekilleştirilir.
            </div>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setLoading(true);
                setError('');
                setImportReport(null);
                try {
                  const form = new FormData();
                  if (selectedListId) form.append('list_id', selectedListId);
                  form.append('file', file);
                  const res = await fetch('/api/bulk/contacts/import', { method: 'POST', headers, body: form });
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok || data?.success === false) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
                  setImportReport(data.data);
                  await loadContacts(1);
                } catch (err) {
                  setError(err.message);
                } finally {
                  setLoading(false);
                }
              }}
            />
            {importReport && (
              <div style={{ marginTop: '10px', background: '#f8f9fa', padding: '10px', borderRadius: '10px', fontSize: '13px' }}>
                <div>Toplam: <strong>{importReport.total_rows}</strong></div>
                <div>Eklenen: <strong>{importReport.inserted_count}</strong></div>
                <div>Mükerrer: <strong>{importReport.duplicate_count}</strong></div>
                <div>Geçersiz: <strong>{importReport.invalid_count}</strong></div>
              </div>
            )}
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: isMobile ? '12px' : '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <div style={{ fontWeight: 700, marginBottom: '10px' }}>Kayıtlar</div>
            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <input
                value={contactsQuery}
                onChange={(e) => setContactsQuery(e.target.value)}
                placeholder="Ara: telefon veya isim"
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              />
              <button
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    await loadContacts(1);
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                Ara
              </button>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
              <select
                value={addToListId}
                onChange={(e) => setAddToListId(e.target.value)}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              >
                <option value="">(Grupsuz)</option>
                {lists.map((l) => (
                  <option key={l.id} value={String(l.id)}>
                    {l.name}
                  </option>
                ))}
              </select>
              <input
                value={addPhone}
                onChange={(e) => setAddPhone(e.target.value)}
                placeholder="Telefon"
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              />
              <input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="İsim (ops.)"
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              />
              <button
                onClick={async () => {
                  if (!addPhone.trim()) return;
                  setLoading(true);
                  setError('');
                  try {
                    await apiSend('/api/bulk/contacts', 'POST', {
                      list_id: addToListId || null,
                      phone: addPhone.trim(),
                      display_name: addName.trim() || null,
                    });
                    setAddPhone('');
                    setAddName('');
                    await loadContacts(1);
                    await loadLists();
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ padding: '10px 14px', borderRadius: '8px', border: 'none', background: '#007bff', color: '#fff', cursor: 'pointer', fontWeight: 700 }}
              >
                Ekle
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Toplam: <strong>{contactsTotal}</strong> • Sayfa: <strong>{contactsPage}</strong>
            </div>
            <div style={{ maxHeight: '360px', overflow: 'auto', border: '1px solid #f1f1f1', borderRadius: '10px', width: '100%', maxWidth: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px', tableLayout: isMobile ? 'auto' : 'auto' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Telefon</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>İsim</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Müşteri Grubu</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Kaynak</th>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.id} style={{ borderTop: '1px solid #eee' }}>
                        <td style={{ padding: isMobile ? '8px' : '10px', fontFamily: 'monospace' }}>
                        {editingContactId === c.id ? (
                          <input
                            value={editPhone}
                            onChange={(e) => setEditPhone(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontFamily: 'monospace' }}
                          />
                        ) : (
                          c.phone_e164
                        )}
                      </td>
                      <td style={{ padding: isMobile ? '8px' : '10px' }}>
                        {editingContactId === c.id ? (
                          <input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            style={{ width: '100%', padding: isMobile ? '6px' : '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: isMobile ? '12px' : '14px' }}
                          />
                        ) : (
                          c.display_name || '-'
                        )}
                      </td>
                      <td style={{ padding: isMobile ? '8px' : '10px' }}>
                        {editingContactId === c.id ? (
                          <select
                            value={editListId}
                            onChange={(e) => setEditListId(e.target.value)}
                            style={{ width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd' }}
                          >
                            <option value="">(Grupsuz)</option>
                            {lists.map((l) => (
                              <option key={l.id} value={String(l.id)}>
                                {l.name}
                              </option>
                            ))}
                          </select>
                        ) : (
                          c.list_name || 'Grupsuz'
                        )}
                      </td>
                      <td style={{ padding: isMobile ? '8px' : '10px' }}>{c.source}</td>
                      <td style={{ padding: isMobile ? '8px' : '10px', whiteSpace: 'nowrap' }}>
                        {editingContactId === c.id ? (
                          <>
                            <button
                              onClick={async () => {
                                setLoading(true);
                                setError('');
                                try {
                                  await apiSend(`/api/bulk/contacts/${c.id}`, 'PATCH', {
                                    phone: editPhone.trim(),
                                    display_name: editName.trim() || null,
                                    list_id: editListId || null
                                  });
                                  setEditingContactId(null);
                                  await loadContacts(1);
                                  await loadLists();
                                } catch (e) {
                                  setError(e.message);
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              style={{ padding: '6px 10px', borderRadius: '8px', border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer', fontWeight: 700, marginRight: '6px' }}
                            >
                              Kaydet
                            </button>
                            <button
                              onClick={() => setEditingContactId(null)}
                              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
                            >
                              Vazgeç
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => {
                                setEditingContactId(c.id);
                                setEditPhone(c.phone_raw || c.phone_e164 || '');
                                setEditName(c.display_name || '');
                                setEditListId(c.list_id ? String(c.list_id) : '');
                              }}
                              style={{ padding: '6px 10px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer', marginRight: '6px' }}
                            >
                              Düzenle
                            </button>
                            <button
                              onClick={async () => {
                                if (!window.confirm('Bu kayıt silinsin mi?')) return;
                                setLoading(true);
                                setError('');
                                try {
                                  const res = await fetch(`/api/bulk/contacts/${c.id}`, { method: 'DELETE', headers });
                                  const data = await res.json().catch(() => ({}));
                                  if (!res.ok || data?.success === false) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
                                  await loadContacts(1);
                                  await loadLists();
                                } catch (e) {
                                  setError(e.message);
                                } finally {
                                  setLoading(false);
                                }
                              }}
                              style={{ padding: '6px 10px', borderRadius: '8px', border: 'none', background: '#dc3545', color: '#fff', cursor: 'pointer' }}
                            >
                              Sil
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                  {contacts.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '12px', color: '#666' }}>
                        Kayıt yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'messages' && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: isMobile ? '12px' : '16px', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>
            <div style={{ fontWeight: 700, marginBottom: '10px' }}>Yeni Gönderi</div>

            <div style={{ display: 'grid', gap: '10px' }}>
              <input
                value={campaignName}
                onChange={(e) => setCampaignName(e.target.value)}
                placeholder="Gönderi Başlığı (Müşteri göremez)"
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
              />
              <textarea
                value={campaignMessage}
                onChange={(e) => setCampaignMessage(e.target.value)}
                placeholder="Mesaj içeriği (emoji destekli)"
                rows={5}
                style={{ padding: '10px', borderRadius: '8px', border: '1px solid #ddd', resize: 'vertical' }}
              />
              <div style={{ marginTop: '-4px', fontSize: '12px', color: '#666' }}>
                Değişken: <code>[musteri-adi]</code> (rehberde kayıtlı isim ile otomatik doldurulur)
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Mesajlarınız hangi saatlerde gönderilsin? (Başlangıç)</div>
                  <select value={startHour} onChange={(e) => setStartHour(e.target.value)} style={{ padding: '10px', borderRadius: '8px' }}>
                    {hourOptions.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>Mesajlarınız hangi saatlerde gönderilsin? (Bitiş)</div>
                  <select value={endHour} onChange={(e) => setEndHour(e.target.value)} style={{ padding: '10px', borderRadius: '8px' }}>
                    {hourOptions.map((h) => (
                      <option key={h} value={h}>
                        {h}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ fontSize: '13px', color: '#666' }}>
                Bu mesai penceresinde günlük kapasite: <strong>{dailyCapacity}</strong> mesaj (10/saat).
              </div>

              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '13px' }}>
                <div style={{ width: '100%', color: '#666', fontSize: '12px' }}>Gönderilecek günler:</div>
                {[
                  [1, 'Pzt'],
                  [2, 'Sal'],
                  [3, 'Çar'],
                  [4, 'Per'],
                  [5, 'Cum'],
                  [6, 'Cmt'],
                  [7, 'Paz'],
                ].map(([d, label]) => (
                  <label key={d} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={days.includes(d)}
                      onChange={(e) => {
                        setDays((prev) => (e.target.checked ? Array.from(new Set([...prev, d])).sort() : prev.filter((x) => x !== d)));
                      }}
                    />
                    {label}
                  </label>
                ))}
              </div>

              <div style={{ borderTop: '1px solid #eee', paddingTop: '10px' }}>
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>Alıcı seçimi</div>
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <select value={recipientMode} onChange={(e) => setRecipientMode(e.target.value)} style={{ padding: '10px', borderRadius: '8px' }}>
                    <option value="manual">Manuel (telefon listesi)</option>
                    <option value="contact_list">Rehber grupları</option>
                  </select>
                </div>
                {recipientMode === 'contact_list' && (
                  <div style={{ display: 'grid', gap: '8px', padding: '10px', border: '1px solid #eee', borderRadius: '10px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>Bir veya birden fazla grup seçebilirsiniz:</div>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                      {lists.map((l) => {
                        const id = Number(l.id);
                        const checked = selectedListIds.includes(id);
                        return (
                          <label key={l.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setSelectedListIds((prev) =>
                                  e.target.checked ? Array.from(new Set([...prev, id])) : prev.filter((x) => x !== id)
                                );
                              }}
                            />
                            {l.name}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
                {recipientMode === 'manual' && (
                  <textarea
                    value={manualPhones}
                    onChange={(e) => setManualPhones(e.target.value)}
                    placeholder="Telefonları satır satır veya virgülle gir"
                    rows={4}
                    style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', resize: 'vertical' }}
                  />
                )}
                <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                  {campaignRecipientLabel}: <strong>{campaignRecipientCount}</strong> • Toplam <strong>{campaignRecipientCount}</strong> kişiye gönderilecek
                </div>
              </div>

              <button
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  setInfo('');
                  try {
                    const payload = {
                      name: campaignName,
                      message_text: campaignMessage,
                      business_hours: { start: startHour, end: endHour, days, timezone: 'Europe/Istanbul' },
                      recipients:
                        recipientMode === 'manual'
                          ? { mode: 'manual', phones: parsePhones(manualPhones) }
                          : { mode: 'contact_list', list_ids: selectedListIds },
                    };
                    const created = await apiSend('/api/bulk/campaigns', 'POST', payload);
                    setInfo('Kampanya oluşturuldu.');
                    setCampaignName('');
                    setCampaignMessage('');
                    setManualPhones('');
                    await loadCampaigns();

                    // otomatik start
                    await apiSend(`/api/bulk/campaigns/${created.data.id}/start`, 'POST');
                    await loadCampaigns();
                    await loadDailyStatus();
                    setInfo('Kampanya başlatıldı (scheduled_at üretildi).');
                    setTimeout(() => setInfo(''), 2500);
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ padding: '12px 14px', borderRadius: '10px', border: 'none', background: '#28a745', color: '#fff', cursor: 'pointer', fontWeight: 800 }}
              >
                Oluştur ve Başlat
              </button>
            </div>
          </div>

          <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <div style={{ fontWeight: 700 }}>Toplu Mesajlar</div>
              <button
                onClick={async () => {
                  setLoading(true);
                  setError('');
                  try {
                    await loadCampaigns();
                    await loadDailyStatus();
                  } catch (e) {
                    setError(e.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
              >
                Yenile
              </button>
            </div>

            <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
              Toplam: <strong>{campaignsTotal}</strong>
            </div>

            <div style={{ maxHeight: '520px', overflow: 'auto', border: '1px solid #f1f1f1', borderRadius: '10px', width: '100%', maxWidth: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px', tableLayout: 'fixed' }}>
                  <thead>
                    <tr style={{ background: '#f8f9fa' }}>
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px', width: isMobile ? '25%' : '25%' }}>Ad</th>
                      {isMobile ? (
                        <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px', width: '35%' }}>Durum Tahmin</th>
                      ) : (
                        <>
                          <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px', width: '15%' }}>Durum</th>
                          <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px', width: '30%' }}>Tahmin</th>
                        </>
                      )}
                      <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px', width: isMobile ? '40%' : '30%' }}>İşlem</th>
                    </tr>
                  </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: isMobile ? '8px' : '10px', wordBreak: 'break-word' }}>
                        <div style={{ fontWeight: 700 }}>{c.name}</div>
                        <div style={{ fontSize: '12px', color: '#666' }}>#{c.id}</div>
                      </td>
                      {isMobile ? (
                        <td style={{ padding: isMobile ? '8px' : '10px' }}>
                          <div style={{ fontWeight: 600, marginBottom: '4px' }}>{statusTr(c.status)}</div>
                          <div style={{ fontSize: '11px', color: '#666', wordBreak: 'break-word' }}>
                            {c.estimated_start_at ? new Date(c.estimated_start_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            {c.estimated_finish_at && c.estimated_start_at !== c.estimated_finish_at && (
                              <> → {new Date(c.estimated_finish_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                            )}
                          </div>
                        </td>
                      ) : (
                        <>
                          <td style={{ padding: isMobile ? '8px' : '10px', whiteSpace: 'nowrap' }}>{statusTr(c.status)}</td>
                          <td style={{ padding: isMobile ? '8px' : '10px', fontSize: '12px', color: '#666', wordBreak: 'break-word' }}>
                            {c.estimated_start_at ? new Date(c.estimated_start_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}
                            {c.estimated_finish_at && c.estimated_start_at !== c.estimated_finish_at && (
                              <> → {new Date(c.estimated_finish_at).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                            )}
                          </td>
                        </>
                      )}
                      <td style={{ padding: isMobile ? '8px' : '10px' }}>
                        <div style={{ display: 'flex', gap: isMobile ? '4px' : '6px', flexWrap: 'wrap' }}>
                          <button
                            onClick={async () => {
                              setLoading(true);
                              setError('');
                              try {
                                await apiSend(`/api/bulk/campaigns/${c.id}/start`, 'POST');
                                await loadCampaigns();
                                await loadDailyStatus();
                              } catch (e) {
                                setError(e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ 
                              padding: isMobile ? '5px 8px' : '6px 10px', 
                              borderRadius: '6px', 
                              border: '1px solid #ddd', 
                              background: '#fff', 
                              cursor: 'pointer', 
                              fontSize: isMobile ? '11px' : '13px', 
                              whiteSpace: 'nowrap',
                              width: isMobile ? 'calc(50% - 2px)' : 'auto',
                              flex: isMobile ? '0 0 calc(50% - 2px)' : 'none'
                            }}
                          >
                            Başlat
                          </button>
                          <button
                            onClick={async () => {
                              setLoading(true);
                              setError('');
                              try {
                                await apiSend(`/api/bulk/campaigns/${c.id}/pause`, 'POST');
                                await loadCampaigns();
                                await loadDailyStatus();
                              } catch (e) {
                                setError(e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ 
                              padding: isMobile ? '5px 8px' : '6px 10px', 
                              borderRadius: '6px', 
                              border: '1px solid #ddd', 
                              background: '#fff', 
                              cursor: 'pointer', 
                              fontSize: isMobile ? '11px' : '13px', 
                              whiteSpace: 'nowrap',
                              width: isMobile ? 'calc(50% - 2px)' : 'auto',
                              flex: isMobile ? '0 0 calc(50% - 2px)' : 'none'
                            }}
                          >
                            Durdur
                          </button>
                          <button
                            onClick={async () => {
                              setLoading(true);
                              setError('');
                              try {
                                await apiSend(`/api/bulk/campaigns/${c.id}/resume`, 'POST');
                                await loadCampaigns();
                                await loadDailyStatus();
                              } catch (e) {
                                setError(e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ 
                              padding: isMobile ? '5px 8px' : '6px 10px', 
                              borderRadius: '6px', 
                              border: '1px solid #ddd', 
                              background: '#fff', 
                              cursor: 'pointer', 
                              fontSize: isMobile ? '11px' : '13px', 
                              whiteSpace: 'nowrap',
                              width: isMobile ? 'calc(50% - 2px)' : 'auto',
                              flex: isMobile ? '0 0 calc(50% - 2px)' : 'none'
                            }}
                          >
                            Devam
                          </button>
                          <button
                            onClick={async () => {
                              setLoading(true);
                              setError('');
                              try {
                                await apiSend(`/api/bulk/campaigns/${c.id}/cancel`, 'POST');
                                await loadCampaigns();
                                await loadDailyStatus();
                              } catch (e) {
                                setError(e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ 
                              padding: isMobile ? '5px 8px' : '6px 10px', 
                              borderRadius: '6px', 
                              border: '1px solid #ddd', 
                              background: '#fff', 
                              cursor: 'pointer', 
                              fontSize: isMobile ? '11px' : '13px', 
                              whiteSpace: 'nowrap',
                              width: isMobile ? 'calc(50% - 2px)' : 'auto',
                              flex: isMobile ? '0 0 calc(50% - 2px)' : 'none'
                            }}
                          >
                            İptal
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Kampanya silinsin mi? (alıcılar da silinir)')) return;
                              setLoading(true);
                              setError('');
                              try {
                                const res = await fetch(`/api/bulk/campaigns/${c.id}`, { method: 'DELETE', headers });
                                const data = await res.json().catch(() => ({}));
                                if (!res.ok || data?.success === false) throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
                                await loadCampaigns();
                                await loadDailyStatus();
                              } catch (e) {
                                setError(e.message);
                              } finally {
                                setLoading(false);
                              }
                            }}
                            style={{ 
                              padding: isMobile ? '5px 8px' : '6px 10px', 
                              borderRadius: '6px', 
                              border: 'none', 
                              background: '#dc3545', 
                              color: '#fff', 
                              cursor: 'pointer', 
                              fontSize: isMobile ? '11px' : '13px', 
                              whiteSpace: 'nowrap',
                              width: isMobile ? 'calc(50% - 2px)' : 'auto',
                              flex: isMobile ? '0 0 calc(50% - 2px)' : 'none'
                            }}
                          >
                            Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {campaigns.length === 0 && (
                    <tr>
                      <td colSpan={isMobile ? 3 : 4} style={{ padding: '12px', color: '#666' }}>
                        Henüz gönderilmiş bir mesajınız bulunmuyor.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {tab === 'status' && (
        <div style={{ border: '1px solid #eee', borderRadius: '12px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontWeight: 700 }}>Durum (Günlük)</div>
            <button
              onClick={async () => {
                setLoading(true);
                setError('');
                try {
                  await loadDailyStatus();
                } catch (e) {
                  setError(e.message);
                } finally {
                  setLoading(false);
                }
              }}
              style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' }}
            >
              Yenile
            </button>
          </div>

          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px', fontSize: '13px' }}>
            <div>Bugün planlanan: <strong>{dailyStatus?.summary?.scheduled_today ?? 0}</strong></div>
            <div>Bugün gönderilen: <strong>{dailyStatus?.summary?.sent_today ?? 0}</strong></div>
            <div>Bugün hatalı: <strong>{dailyStatus?.summary?.failed_today ?? 0}</strong></div>
          </div>

          <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
            Son 50 kayıt (hata sebebi dahil):
          </div>
          <div style={{ maxHeight: '520px', overflow: 'auto', border: '1px solid #f1f1f1', borderRadius: '10px', width: '100%', maxWidth: '100%' }}>
            <div style={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: isMobile ? '12px' : '13px', tableLayout: isMobile ? 'auto' : 'auto' }}>
                <thead>
                  <tr style={{ background: '#f8f9fa' }}>
                    <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Gönderi</th>
                    <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Telefon</th>
                    <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Durum</th>
                    <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Zaman</th>
                    <th style={{ textAlign: 'left', padding: isMobile ? '8px' : '10px' }}>Hata</th>
                  </tr>
                </thead>
              <tbody>
                {(dailyStatus?.recent || []).map((r) => (
                  <tr key={r.id} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: '10px' }}>
                      <div style={{ fontWeight: 700 }}>{r.title || `#${r.campaign_id}`}</div>
                      <div style={{ fontSize: '12px', color: '#666' }}>#{r.campaign_id}</div>
                    </td>
                    <td style={{ padding: '10px', fontFamily: 'monospace' }}>{r.phone_e164}</td>
                    <td style={{ padding: '10px' }}>{recipientStatusTr(r.status)}</td>
                    <td style={{ padding: '10px', fontSize: '12px', color: '#666' }}>
                      {r.sent_at ? new Date(r.sent_at).toLocaleString('tr-TR') : (r.scheduled_at ? new Date(r.scheduled_at).toLocaleString('tr-TR') : '-')}
                    </td>
                    <td style={{ padding: '10px', color: String(r.status).toLowerCase() === 'failed' ? '#b00020' : '#666', fontSize: '12px' }}>
                      {r.last_error || '-'}
                    </td>
                  </tr>
                ))}
                {(!dailyStatus?.recent || dailyStatus.recent.length === 0) && (
                  <tr>
                    <td colSpan={5} style={{ padding: '12px', color: '#666' }}>
                      Bugün kayıt yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


